import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  fetchDirectoryVehicles,
  permanentlyDeleteDirectoryVehicle,
} from "../../../api/adminDirectoryApi";
import AdminVendorsWorkspace from "../vendors/AdminVendorsWorkspace";

/* --------------------- helpers --------------------- */

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function cleanStr(x) {
  const s = safeStr(x).trim();
  return s ? s : "";
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function moneyINR(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "₹0";
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}
function pct01(x, digits = 0) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}
function dtClean(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
function useDebouncedValue(value, delayMs = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function labelVehicle(x) {
  const reg = cleanStr(x?.regNo);
  const title = cleanStr([x?.brand, x?.model].filter(Boolean).join(" "));
  if (title && reg) return `${title} • ${reg}`;
  return title || reg || "—";
}

/* --------------------- UI primitives --------------------- */

function Chip({ tone = "slate", children }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-800 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold leading-none whitespace-nowrap",
        cls
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ title, value, hint, accent = "slate" }) {
  const ring =
    accent === "green"
      ? "ring-emerald-100"
      : accent === "red"
      ? "ring-rose-100"
      : accent === "amber"
      ? "ring-amber-100"
      : accent === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  const dot =
    accent === "green"
      ? "bg-emerald-500"
      : accent === "red"
      ? "bg-rose-500"
      : accent === "amber"
      ? "bg-amber-500"
      : accent === "blue"
      ? "bg-sky-500"
      : "bg-slate-700";

  return (
    <div className={classNames("rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4", ring)}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
          {title}
        </div>
        <span className={classNames("h-2 w-2 rounded-full", dot)} aria-hidden="true" />
      </div>

      <div className="mt-1 text-2xl font-semibold text-slate-900 whitespace-nowrap">{value}</div>
      {hint ? (
        <div className="mt-1 text-xs text-slate-500 whitespace-nowrap overflow-hidden text-ellipsis">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function SimpleBarList({ items = [], valueKey = "value", rightRenderer }) {
  const maxV = Math.max(1, ...items.map((x) => num(x?.[valueKey])));
  return (
    <div className="space-y-2">
      {items.map((x, idx) => {
        const v = num(x?.[valueKey]);
        const w = Math.round((v / maxV) * 100);
        return (
          <div key={idx} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{labelVehicle(x)}</div>
              </div>
              <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                {rightRenderer ? rightRenderer(x) : v.toLocaleString("en-IN")}
              </div>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-slate-700" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
      {items.length === 0 ? <div className="text-sm text-slate-500">No data.</div> : null}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/* ✅ Your exact pagination footer style */
function PaginationFooter({ page, size, totalPages, totalItems, onPrev, onNext, onSize }) {
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500 whitespace-nowrap">
          Showing <span className="font-semibold text-slate-700">{size}</span> • Total{" "}
          <span className="font-semibold text-slate-700">{totalItems}</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500 whitespace-nowrap">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                ▼
              </span>
            </div>
          </div>

          <div className="text-sm text-slate-700 whitespace-nowrap">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={!canNext}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------- CSV export --------------------- */

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}
function downloadTextFile(filename, text, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- compliance helpers --------------------- */

function complianceTone(st) {
  const s = safeStr(st).toUpperCase();
  if (s === "APPROVED_VERIFIED") return "green";
  if (s === "ACTION_REQUIRED") return "red";
  if (s === "PENDING_ADMIN_REVIEW") return "amber";
  if (s === "SUBMITTED") return "blue";
  return "slate";
}
function complianceLabel(st) {
  const s = safeStr(st).toUpperCase();
  if (!s) return "DRAFT";
  return s.replaceAll("_", " ");
}

/* --------------------- page --------------------- */

export default function DirectoryVehicles() {
  const [directoryTab, setDirectoryTab] = useState("OWNER");
  const [ownerVehicleType, setOwnerVehicleType] = useState("CAR");
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [status, setStatus] = useState("ALL"); // compliance status
  const [risk, setRisk] = useState("ALL"); // ATTENTION only
  const [ownerLink, setOwnerLink] = useState("ALL"); // LINKED/UNLINKED

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [resp, setResp] = useState(null);
  const [deletingReg, setDeletingReg] = useState("");

  const fetchSeq = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [qDebounced, status, risk, ownerLink, ownerVehicleType, size]);

  useEffect(() => {
    if (directoryTab !== "OWNER") {
      setLoading(false);
      return undefined;
    }

    let alive = true;
    const seq = ++fetchSeq.current;

    async function run() {
      setLoading(true);
      setErr("");

      try {
        const data = await fetchDirectoryVehicles({
          q: qDebounced,
          status: status === "ALL" ? undefined : status,
          risk: risk === "ALL" ? undefined : risk,
          owner: ownerLink === "ALL" ? undefined : ownerLink,
          vehicleType: ownerVehicleType,
          page,
          size,
        });

        if (!alive || seq !== fetchSeq.current) return;
        setResp(data || null);
      } catch (e) {
        if (!alive || seq !== fetchSeq.current) return;
        setErr(e?.message || "Failed to load directory vehicles");
        setResp(null);
      } finally {
        if (!alive || seq !== fetchSeq.current) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [directoryTab, qDebounced, status, risk, ownerLink, ownerVehicleType, page, size]);

  const summary = resp?.summary || null;
  const items = resp?.items || [];
  const totalItems = num(resp?.totalItems);
  const totalPages = num(resp?.totalPages);
  const currentPage = num(resp?.page);
  const pageSize = num(resp?.size);

  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
    const filename = `vehicles-directory-page-${currentPage + 1}-${stamp}.csv`;

    const header = [
      "RegNo",
      "Brand",
      "Model",
      "Colour",
      "YearOfPurchase",
      "OwnerId",
      "OwnerName",
      "OwnerPhone",
      "OwnerLocation",
      "VehicleLocation",
      "ComplianceStatus",
      "Bookings",
      "Rides",
      "LifetimeFare",
      "LifetimeOwnerPayout",
      "Cancellations",
      "LastActivityAt",
    ];

    const lines = [header.map(csvEscape).join(",")];

    items.forEach((v) => {
      lines.push(
        [
          v.regNo,
          v.brand,
          v.model,
          v.colour,
          v.yearOfPurchase,
          v.ownerId ?? "",
          v.ownerName ?? "",
          v.ownerPhone ?? "",
          v.ownerLocation ?? "",
          v.vehicleLocation ?? "",
          v.complianceStatus ?? "",
          num(v.bookingCount),
          num(v.rideCount),
          num(v.lifetimeFareTotal),
          num(v.lifetimeOwnerPayoutTotal),
          num(v.cancellationCount),
          v.lastActivityAt || "",
        ]
          .map(csvEscape)
          .join(",")
      );
    });

    downloadTextFile(filename, lines.join("\n"));
  };

  const handlePermanentDelete = async (vehicle) => {
    const reg = cleanStr(vehicle?.regNo);
    if (!reg) return;

    const typed = window.prompt(`Type ${reg} to permanently delete this vehicle.`);
    if (typed !== reg) return;

    const reason = window.prompt("Reason for permanent delete (optional):") || "";

    try {
      setDeletingReg(reg);
      const res = await permanentlyDeleteDirectoryVehicle(reg, { reason });
      if (!res?.deleted) {
        window.alert(res?.message || "Vehicle could not be deleted.");
        return;
      }

      const data = await fetchDirectoryVehicles({
        q: qDebounced,
        status: status === "ALL" ? undefined : status,
        risk: risk === "ALL" ? undefined : risk,
        owner: ownerLink === "ALL" ? undefined : ownerLink,
        vehicleType: ownerVehicleType,
        page,
        size,
      });
      setResp(data || null);
      window.alert(res?.message || `${reg} deleted permanently.`);
    } catch (e) {
      window.alert(e?.message || "Failed to delete vehicle");
    } finally {
      setDeletingReg("");
    }
  };

  const topBlock = useMemo(() => {
    if (!summary) return null;

    const totalVehicles = num(summary.totalVehicles).toLocaleString("en-IN");
    const verified = num(summary.verifiedVehicles).toLocaleString("en-IN");

    const bookings7d = num(summary.bookings7d).toLocaleString("en-IN");
    const rides7d = num(summary.rides7d).toLocaleString("en-IN");
   

    const totalFare = moneyINR(summary.totalFareAllTime);
    const totalPayout = moneyINR(summary.totalOwnerPayoutAllTime);

    const cancelRate = pct01(summary.cancellationRate, 0);
    const cancelHint = `${num(summary.cancelledBookingsAllTime).toLocaleString("en-IN")} / ${num(
      summary.totalBookingsAllTime
    ).toLocaleString("en-IN")} bookings`;

    const maxC = summary.maxCancelledVehicle || null;
    const maxCancelValue = maxC
      ? `${num(maxC.cancelCount).toLocaleString("en-IN")} (${pct01(maxC.cancelRate, 0)})`
      : "—";
    const maxCancelHint = maxC ? labelVehicle(maxC) : "—";

    return (
      <div className="space-y-4">
        {/* KPI strip (clean) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">


          <KpiCard title="Total Vehicles" value={totalVehicles} hint="All time" accent="blue" />
          <KpiCard title="Verified Vehicles" value={verified} hint="APPROVED_VERIFIED" accent="green" />
          <KpiCard title="Bookings (7d)" value={bookings7d} hint="Created in last 7 days" accent="blue" />
          <KpiCard title="Rides (7d)" value={rides7d} hint="Created in last 7 days" accent="blue" />
         

          <KpiCard title="Total Fare (lifetime)" value={totalFare} hint="Excludes cancelled/unfulfilled" accent="blue" />
          <KpiCard title="Owner Payout (lifetime)" value={totalPayout} hint="SUCCESS payouts" accent="green" />
          <KpiCard title="Cancellation rate" value={cancelRate} hint={cancelHint} accent="red" />
          <KpiCard title="Max cancellations" value={maxCancelValue} hint={maxCancelHint} accent="red" />
        </div>

        {/* Top charts section */}
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top booked vehicles" subtitle="Most bookings (all time)">
              <SimpleBarList items={(summary.topBookedVehicles || []).slice(0, 3)} valueKey="value" />

            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top ride vehicles" subtitle="Most rides (all time)">
            <SimpleBarList items={(summary.topRideVehicles || []).slice(0, 3)} valueKey="value" />

            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-4">
            <ChartCard title="Top payout vehicles" subtitle="Highest owner payouts (SUCCESS)">
             <SimpleBarList
  items={(summary.topPayoutVehicles || []).slice(0, 3)}
  valueKey="amount"
  rightRenderer={(x) => moneyINR(x?.amount)}
/>

            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-6">
            <ChartCard title="Top earned vehicles" subtitle="Fare + Owner payout together">
              <div className="space-y-2">
               {(summary.topEarnedVehicles || []).slice(0, 3).map((x, idx) => {

                  const fare = num(x?.fareTotal);
                  const pay = num(x?.ownerPayoutTotal);
                  const label = labelVehicle(x);
                  return (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
                        <div className="flex items-center gap-4 text-sm font-semibold text-slate-800 whitespace-nowrap">
                          <span>Fare: {moneyINR(fare)}</span>
                          <span>Payout: {moneyINR(pay)}</span>
                        </div>
                      </div>
                      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-slate-700"
                          style={{
                            width: `${Math.round(
                              (fare / Math.max(1, ...((summary.topEarnedVehicles || []).map((z) => num(z?.fareTotal))))) *
                                100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                {(summary.topEarnedVehicles || []).length === 0 ? (
                  <div className="text-sm text-slate-500">No data.</div>
                ) : null}
              </div>
            </ChartCard>
          </div>

          <div className="col-span-12 2xl:col-span-6">
            <ChartCard title="Most cancelled vehicles" subtitle="Cancellation count + % per vehicle">
              <div className="space-y-2">
                {(summary.topCancelledVehicles || []).slice(0, 3).map((x, idx) => {

                  const cancels = num(x?.cancelCount);
                  const rate = num(x?.cancelRate);
                  return (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900 truncate">{labelVehicle(x)}</div>
                        <div className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                          {cancels.toLocaleString("en-IN")} • {pct01(rate, 0)}
                        </div>
                      </div>
                      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-rose-500" style={{ width: `${Math.round(rate * 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
                {(summary.topCancelledVehicles || []).length === 0 ? (
                  <div className="text-sm text-slate-500">No data.</div>
                ) : null}
              </div>
            </ChartCard>
          </div>
        </div>
      </div>
    );
  }, [summary]);

  return (
    <div className="min-w-0">
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "OWNER", label: "Owner Vehicle" },
            { key: "VENDOR", label: "Vendor Vehicle" },
          ].map((tab) => {
            const active = directoryTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setDirectoryTab(tab.key)}
                className={classNames(
                  "inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition",
                  active
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {directoryTab === "OWNER" ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { key: "CAR", label: "Car" },
              { key: "AUTO", label: "Auto" },
              { key: "E_RICKSHAW", label: "E Rikshaw" },
              { key: "VAN", label: "Van" },
            ].map((tab) => {
              const active = ownerVehicleType === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setOwnerVehicleType(tab.key)}
                  className={classNames(
                    "inline-flex h-10 items-center rounded-md border px-4 text-sm font-semibold transition",
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {directoryTab === "VENDOR" ? (
        <AdminVendorsWorkspace
          title="Vendor Vehicles"
          subtitle="Vendor-linked supply cars stay in their own directory lane and do not mix with owner vehicle compliance or owner vehicle inventory."
          compactHeader
        />
      ) : (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                Owner Vehicles Directory
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Owner vehicles only. Vendor cars stay in the separate vendor vehicle tab.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Chip tone="blue">ADMIN ACTIONS</Chip>
              <Chip>GLOBAL</Chip>
            </div>
          </div>

          {/* KPI + charts */}
          <div className="mt-5">{topBlock}</div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:flex xl:flex-wrap xl:items-end">
              <div className="w-full xl:min-w-[320px] xl:flex-1">
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Reg No / owner ID / owner phone / owner name / brand / model / location"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Compliance</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="APPROVED_VERIFIED">Approved / Verified</option>
                  <option value="PENDING_ADMIN_REVIEW">Pending admin review</option>
                  <option value="ACTION_REQUIRED">Action required</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="DRAFT">Draft</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Owner link</div>
                <select
                  value={ownerLink}
                  onChange={(e) => setOwnerLink(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="LINKED">Linked</option>
                  <option value="UNLINKED">Unlinked</option>
                </select>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Risk</div>
                <select
                  value={risk}
                  onChange={(e) => setRisk(e.target.value)}
                  className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 hover:bg-slate-50"
                >
                  <option value="ALL">All</option>
                  <option value="ATTENTION">Attention only</option>
                </select>
              </div>
            </div>

            {/* right actions */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
              <div className="text-xs text-slate-500 whitespace-nowrap">
                Showing: <span className="text-slate-800">{loading ? "…" : items.length}</span> vehicles
              </div>

              <button
                onClick={exportCsv}
                disabled={loading || items.length === 0}
                className="inline-flex h-10 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-[1880px] w-full">
            <thead className="bg-white sticky top-0 z-10">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                <th className="px-6 py-3 w-[160px]">Reg No</th>

                {/* ✅ independent vehicle columns */}
                <th className="px-6 py-3 w-[160px]">Brand</th>
                <th className="px-6 py-3 w-[160px]">Model</th>
                <th className="px-6 py-3 w-[140px]">Colour</th>
                <th className="px-6 py-3 w-[140px]">Year</th>

                {/* ✅ owner only */}
                <th className="px-6 py-3 w-[120px]">Owner ID</th>
                <th className="px-6 py-3 w-[220px]">Owner name</th>
                <th className="px-6 py-3 w-[160px]">Owner phone</th>
                <th className="px-6 py-3 w-[220px]">Owner location</th>
                <th className="px-6 py-3 w-[220px]">Vehicle location</th>

                <th className="px-6 py-3 w-[190px]">Compliance</th>

                <th className="px-6 py-3 w-[120px] text-right">Bookings</th>
                <th className="px-6 py-3 w-[120px] text-right">Rides</th>

                <th className="px-6 py-3 w-[170px] text-right">Lifetime fare</th>
                <th className="px-6 py-3 w-[190px] text-right">Lifetime payout</th>

                <th className="px-6 py-3 w-[140px] text-right">Cancels</th>

                <th className="px-6 py-3 w-[220px]">Last activity</th>
                <th className="px-6 py-3 w-[180px]">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 18 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <div className="h-4 w-full rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : err ? (
                <tr>
                  <td colSpan={18} className="px-6 py-8">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                      {err}
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={18} className="px-6 py-10 text-sm text-slate-600">
                    No vehicles found.
                  </td>
                </tr>
              ) : (
                items.map((v) => {
                  const reg = v.regNo || "—";
                  const comp = v.complianceStatus || "DRAFT";

                  return (
                    <tr key={reg} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 whitespace-nowrap">{reg}</td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.brand || "—"}</td>
                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.model || "—"}</td>
                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.colour || "—"}</td>
                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.yearOfPurchase || "—"}</td>

                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.ownerId ?? "—"}</td>
                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                        {v.ownerName || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-800 whitespace-nowrap">{v.ownerPhone || "—"}</td>
                      <td className="px-6 py-4 text-sm text-slate-700 max-w-[220px] truncate" title={v.ownerLocation || ""}>
                        {v.ownerLocation || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 max-w-[220px] truncate" title={v.vehicleLocation || ""}>
                        {v.vehicleLocation || "—"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <Chip tone={complianceTone(comp)}>{complianceLabel(comp)}</Chip>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(v.bookingCount).toLocaleString("en-IN")}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(v.rideCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {moneyINR(v.lifetimeFareTotal)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {moneyINR(v.lifetimeOwnerPayoutTotal)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-800 text-right whitespace-nowrap">
                        {num(v.cancellationCount).toLocaleString("en-IN")}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                        {dtClean(v.lastActivityAt)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-2">
                          {v.ownerId ? (
                            <NavLink
                              to={`/admin/support/owners/${encodeURIComponent(String(v.ownerId))}`}
                              className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                            >
                              Owner
                            </NavLink>
                          ) : null}
                          <NavLink
                            to={`/admin/compliance?regNo=${encodeURIComponent(String(reg))}`}
                            className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                          >
                            Compliance
                          </NavLink>
                          <button
                            onClick={() => handlePermanentDelete(v)}
                            disabled={deletingReg === reg}
                            className="inline-flex h-9 items-center justify-center rounded-md border border-rose-200 bg-rose-50 px-3 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                          >
                            {deletingReg === reg ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <PaginationFooter
          page={currentPage}
          size={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
          onSize={(n) => setSize(clamp(n, 10, 200))}
        />
      </div>
      )}
    </div>
  );
}
